/**
 * The Lite plan's page allowance, as this computer sees it (docs/planning/lite-tier.md).
 * Renderer-safe: numbers and dates only, never a key, token or page identity.
 *
 * Local balance = the signed snapshot's balance minus the pages counted here that Phone Home has
 * not acknowledged yet, minus pages reserved by captures being read now. Phone Home's snapshot is
 * the authority; this arithmetic only keeps a computer from reading past it while offline.
 */
export type PageAllowanceTier = 'lite-monthly' | 'lite-yearly' | 'custom';

export interface PageAllowancePack {
  readonly pages: number;
  readonly left: number;
  readonly expiresUtc: string;
  /** Expires within 30 days. */
  readonly expiresSoon: boolean;
}

export type PageAllowanceView =
  /** Not a Lite plan, or not known yet: nothing is metered and nothing is shown. */
  | { readonly metered: false; readonly tier: null }
  /** Custom: unlimited. */
  | { readonly metered: false; readonly tier: 'custom' }
  | {
      readonly metered: true;
      readonly tier: 'lite-monthly' | 'lite-yearly';
      readonly periodEndUtc: string;
      readonly included: { readonly pages: number; readonly used: number; readonly left: number };
      readonly packs: readonly PageAllowancePack[];
      /** Pages left by the last snapshot, before local counting. */
      readonly snapshotBalance: number;
      /** Counted here, not yet acknowledged by Phone Home. */
      readonly pendingPages: number;
      /** Held by captures being read now. */
      readonly reservedPages: number;
      /** What new work may still read. */
      readonly localBalance: number;
      readonly warnBelowPages: number;
      /** Phone Home's 90% warning, or the local balance now at or under its line. */
      readonly lowBalance: boolean;
      readonly asOfUtc: string;
      readonly checkinByUtc: string;
      readonly checkinOverdue: boolean;
      /** The partner's customer discount, whole percent, when Phone Home answered one. */
      readonly partnerDiscountPct: number | null;
    };

/** 1,000 pages per pack, $40 list (owner decision 2026-10-14). */
export const pagePackPages = 1_000;
export const pagePackListPriceUsd = 40;
export const pagePackExpiresSoonMilliseconds = 30 * 24 * 60 * 60 * 1_000;

export interface PageSnapshotFacts {
  readonly tier: PageAllowanceTier;
  readonly period: { readonly startUtc: string; readonly endUtc: string } | null;
  readonly included: {
    readonly pages: number;
    readonly used: number;
    readonly left: number;
  } | null;
  readonly packs: readonly {
    readonly pages: number;
    readonly left: number;
    readonly expiresUtc: string;
  }[];
  readonly balancePages: number | null;
  readonly warnBelowPages: number | null;
  readonly lowBalance: boolean;
  readonly asOfUtc: string;
  readonly checkinByUtc: string;
}

/** Balance arithmetic: never below zero. */
export const localPageBalance = (
  snapshotBalance: number,
  pendingPages: number,
  reservedPages: number,
): number => Math.max(0, snapshotBalance - Math.max(0, pendingPages) - Math.max(0, reservedPages));

export const pageAllowanceView = (input: {
  readonly snapshot: PageSnapshotFacts | null;
  readonly pendingPages: number;
  readonly reservedPages: number;
  readonly nowUtc: string;
  readonly partnerDiscountPct?: number | null;
}): PageAllowanceView => {
  const snapshot = input.snapshot;
  if (snapshot === null) return { metered: false, tier: null };
  if (
    snapshot.tier === 'custom' ||
    snapshot.included === null ||
    snapshot.period === null ||
    snapshot.balancePages === null
  )
    return { metered: false, tier: 'custom' };
  const now = Date.parse(input.nowUtc);
  const localBalance = localPageBalance(
    snapshot.balancePages,
    input.pendingPages,
    input.reservedPages,
  );
  const warnBelowPages = snapshot.warnBelowPages ?? 0;
  return {
    metered: true,
    tier: snapshot.tier,
    periodEndUtc: snapshot.period.endUtc,
    included: snapshot.included,
    packs: snapshot.packs.map((pack) => ({
      pages: pack.pages,
      left: pack.left,
      expiresUtc: pack.expiresUtc,
      expiresSoon: Date.parse(pack.expiresUtc) - now <= pagePackExpiresSoonMilliseconds,
    })),
    snapshotBalance: snapshot.balancePages,
    pendingPages: Math.max(0, input.pendingPages),
    reservedPages: Math.max(0, input.reservedPages),
    localBalance,
    warnBelowPages,
    lowBalance: snapshot.lowBalance || localBalance <= warnBelowPages,
    asOfUtc: snapshot.asOfUtc,
    checkinByUtc: snapshot.checkinByUtc,
    checkinOverdue: now >= Date.parse(snapshot.checkinByUtc),
    partnerDiscountPct: input.partnerDiscountPct ?? null,
  };
};

export type PageAdmission =
  | { readonly admit: true }
  | { readonly admit: false; readonly block: 'allowance-used' | 'checkin-overdue' }
  /** Some pages are left but not enough for this capture: it waits, whole. */
  | {
      readonly admit: false;
      readonly block: 'waiting-for-pages';
      readonly needed: number;
      readonly left: number;
    };

/**
 * May new work read `pages` more pages? Custom and unmetered plans always may. An overdue check-in
 * stops new work before anything else; a used-up allowance next; a capture that does not fit waits.
 */
export const pageAdmission = (view: PageAllowanceView, pages: number): PageAdmission => {
  if (!view.metered) return { admit: true };
  if (view.checkinOverdue) return { admit: false, block: 'checkin-overdue' };
  if (view.localBalance <= 0) return { admit: false, block: 'allowance-used' };
  const needed = Math.max(1, Math.ceil(pages));
  if (needed > view.localBalance)
    return { admit: false, block: 'waiting-for-pages', needed, left: view.localBalance };
  return { admit: true };
};

/** The sentence an operator reads when new work waits for pages. */
export const pageAdmissionSentence = (
  admission: Exclude<PageAdmission, { admit: true }>,
): string => {
  switch (admission.block) {
    case 'checkin-overdue':
      return 'Paige has not checked in with Ademero for 30 days, so new documents wait. Connect to the Internet; reading continues after the check-in.';
    case 'allowance-used':
      return "This plan's pages are used up, so new documents wait. Buy pages in Settings, then License, or wait for the allowance to reset.";
    case 'waiting-for-pages':
      return `Waiting for pages. These documents need about ${admission.needed.toLocaleString('en-US')} pages and ${admission.left.toLocaleString('en-US')} are left. They are read automatically after you buy pages or the allowance resets.`;
  }
};

/** Is an error message one of these page holds? (The capture stays waiting and is read again on its own.) */
export const isPageAllowanceHold = (message: string | null | undefined): boolean =>
  typeof message === 'string' &&
  (message.startsWith('Waiting for pages.') ||
    message.startsWith("This plan's pages are used up") ||
    message.startsWith('Paige has not checked in with Ademero'));
